/**
 * The authentication contract.
 *
 * Declared here, in @hrms/shared, because the API, web, and mobile must all agree on the
 * exact shape of a login — and a login is the one place where a shape mismatch is not a rendering
 * bug but a lockout. The API implements this; every client consumes the generated typed client.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { PERMISSIONS } from '../permissions/permissions.js';
import { SCOPES } from '../permissions/roles.js';
import { RESERVED_SUBDOMAINS } from '../lib/subdomain.js';
import { FREE_TRIAL_EMPLOYEE_LIMIT } from '../billing-pricing.js';
import {
  emailSchema,
  errorBodySchema,
  instantSchema,
  localeSchema,
  moduleKeySchema,
  orgIdSchema,
  phoneSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/** The Terms version the signup form presents; sent back as `termsVersion` and stored on acceptance. */
export const CURRENT_TERMS_VERSION = '2026-09-24';

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/**
 * The tenant a login is aimed at.
 *
 * Login is the one request in the system that arrives before a tenant is known, and the org CANNOT
 * be inferred from the email: `people.email` is unique per org, not globally, because the same
 * person may be an employee of one customer of ours and a contractor at another. Inferring would
 * mean either picking one arbitrarily or refusing the ambiguous case — the first is a cross-tenant
 * authentication bug, the second is an oracle that reveals a person belongs to two of our
 * customers. Naming the org explicitly avoids both.
 */
export const orgSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Must be a lowercase slug, e.g. "acme-india"')
  // A company can never claim a slug that collides with a platform-reserved subdomain
  // (admin.<appDomain>, api.<appDomain>, ...) — see lib/subdomain.ts.
  .refine((slug) => !RESERVED_SUBDOMAINS.has(slug), 'This workspace name is reserved.');

/**
 * The floor for a password being SET, not one being presented.
 *
 * Length rather than composition rules, per NIST SP 800-63B: forced symbols and digits produce
 * predictable substitutions ("Password1!") while costing real usability, whereas length is the
 * parameter that actually raises the search space. The 128 cap mirrors the work bound in
 * @hrms/auth — an unbounded "password" is an unbounded argon2 hash and therefore a cheap DoS.
 */
export const newPasswordSchema = z
  .string()
  .min(8, 'Must be at least 8 characters')
  .max(128, 'Must be at most 128 characters');

/**
 * A password being PRESENTED, which is deliberately looser than the one above.
 *
 * Applying today's floor here would reject people whose password predates it — they would be told
 * their correct password is invalid, with no way to reach the change-password endpoint that would
 * fix it. Strength is enforced where a password is chosen, never where one is checked.
 */
export const presentedPasswordSchema = z.string().min(1).max(128);

export const totpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Must be a 6-digit code');

export const recoveryCodeSchema = z.string().trim().min(16).max(32);

/** base64url of 32 random bytes, plus room for a future format change. */
export const refreshTokenSchema = z.string().min(32).max(512);

// ---------------------------------------------------------------------------
// Shared response shapes
// ---------------------------------------------------------------------------

export const tokenPairSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  tokenType: z.literal('Bearer'),
  /** Seconds until `accessToken` expires. Clients refresh ahead of this rather than on 401 alone. */
  expiresIn: z.number().int().positive(),
});
export type TokenPair = z.infer<typeof tokenPairSchema>;

export const authenticatedPersonSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  orgSlug: z.string(),
  /**
   * Null for a phone-only person (added from the owner's contacts at sign-up, no work email): they
   * sign in with their mobile number. Clients show `displayName` and must not assume an email.
   */
  email: z.string().nullable(),
  displayName: z.string(),
  locale: localeSchema,
  photoUrl: z.string().nullable(),
  /** Set after an admin-issued temporary password; the client must route to the change screen. */
  mustChangePassword: z.boolean(),
  /**
   * Whether the credential has a password yet. `false` for an owner who signed up password-less
   * (phone OTP / SSO) and has not run `setInitialPassword`: the web app shows the "set your
   * password" popup. Optional so a handler that predates it still satisfies the schema; absent
   * means "unknown", not "no password".
   */
  hasPassword: z.boolean().optional(),
  mfaEnabled: z.boolean(),
});
export type AuthenticatedPerson = z.infer<typeof authenticatedPersonSchema>;

export const mfaMethodSchema = z.enum(['totp', 'recovery_code']);

/**
 * Login has two successful outcomes, and they are a discriminated union rather than an optional
 * `tokens` field so a client cannot compile while ignoring the MFA branch. A union member that
 * must be narrowed is a type error at the call site; a nullable field is a runtime surprise.
 */
export const loginResultSchema = z.discriminatedUnion('result', [
  z.object({
    result: z.literal('authenticated'),
    tokens: tokenPairSchema,
    person: authenticatedPersonSchema,
  }),
  z.object({
    result: z.literal('mfa_required'),
    methods: z.array(mfaMethodSchema),
  }),
]);
export type LoginResult = z.infer<typeof loginResultSchema>;

export const grantSchema = z.object({
  role: z.string().min(1),
  scope: z.enum(SCOPES),
  scopeId: uuidSchema.nullable(),
});

export const meResponseSchema = z.object({
  person: authenticatedPersonSchema,
  /**
   * Every permission held somewhere, for gating navigation and disabling controls. It is NOT an
   * authorisation decision: scope is resolved per record by `can()`, server-side. A client that
   * treats this list as permission to act will show an approve button that the API then refuses.
   */
  permissions: z.array(z.enum(PERMISSIONS)),
  /**
   * What each role in `grants` means in THIS organisation, custom roles and edited built-in ones
   * included. It lets a client run the same pure `can()` as the server. Without it a client can
   * only fall back to the code defaults, which know nothing about a role an admin created, so a
   * person holding one sees an empty menu. Keys are just the distinct roles in `grants`.
   *
   * Optional so a new web build keeps working against an older API during a rolling deploy.
   * Like `permissions`, it is for gating navigation, never an authorisation decision.
   */
  rolePermissions: z.record(z.string().min(1), z.array(z.enum(PERMISSIONS))).optional(),
  grants: z.array(grantSchema),
  session: z.object({
    id: uuidSchema,
    impersonatedBy: uuidSchema.nullable(),
    /** Set when this session was minted by a platform admin's "enter organisation" grant — drives
     *  the top-nav "viewing as platform owner" banner/switcher, never an authorisation decision. */
    impersonatedByPlatformAdmin: z.boolean(),
  }),
  /** Trial end for the caller's organisation; null/absent for organisations that never had a trial. */
  trialEndsAt: instantSchema.nullable().optional(),
  agreementState: z.enum(['none', 'draft', 'sent', 'signed']).optional(),
  /**
   * True while the organisation is read-only: its free trial ended without a plan, or billing locked
   * it (`setBillingLock`). Everything stays visible; writes are refused. The web puts it on the Actor
   * so `can()` hides write actions exactly as the API refuses them. Absent means not read-only.
   */
  readOnly: z.boolean().optional(),
  /**
   * B-P5 (KEKA_VS_BILLING.md §4.3): the combined entitled-AND-not-switched-off module list — the
   * session layer's read of `Organization.settings.modules`, the same cache
   * `requireModuleEntitlement` gates the API on. The web/mobile menu filters against this, not
   * against a second, independent notion of what the org can see.
   */
  enabledModules: z.array(moduleKeySchema).optional(),
  /**
   * The caller's job title from the assignment in effect today, for the app frame's profile
   * corner. Carried here so the frame does not search the employee list on every page load just
   * to show it. Null when the person has no employment or no designation set.
   *
   * Optional, like `rolePermissions`, so a new web build keeps working against an older API
   * during a rolling deploy.
   */
  designation: z.string().nullable().optional(),
  /**
   * True when a platform admin has linked this account to the same person's account in another
   * organisation (`CrossOrgIdentityLink`). The "Switch company" menu renders only then, and asks
   * for the list of companies only for these people. Optional for rolling deploys, like the above.
   */
  hasLinkedOrganizations: z.boolean().optional(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

// ---------------------------------------------------------------------------
// Request shapes
// ---------------------------------------------------------------------------

export const loginRequestSchema = z.object({
  /**
   * Optional because a company on its own subdomain (`acme.<appDomain>`) already names its
   * tenant in the hostname the request arrived on — the API falls back to that when this is
   * omitted (see apps/api/src/routes/auth.ts). Still required when logging in from the bare app
   * domain, same as today.
   */
  orgSlug: orgSlugSchema.optional(),
  // THE SIGN-IN IDENTIFIER — named `email` only for wire compatibility with every shipped client.
  // Presented, not chosen — so not held to email FORMAT (same reasoning as `presentedPasswordSchema`
  // above): neither the super-admin username, an employee's `people.username` (see
  // employee.contract.ts), nor a mobile number is an email address. The API matches this value
  // against `people.email`, `people.username`, OR `people.phone` within the resolved org (see
  // apps/api/src/routes/auth.ts). A phone-only employee (no email — added from the owner's contacts
  // at sign-up) signs in with their mobile number: the API normalises a phone-looking value to E.164
  // (as `signupPhone.ts` does, defaulting to the org's country for a number typed without its
  // calling code) before matching `people.phone`. Clients label the field "Email or mobile number".
  // Trimmed (mobile keyboards and password managers add trailing spaces) and lowercased to match
  // how `people.email` is stored; a malformed value simply matches no account.
  email: z.string().trim().toLowerCase().min(1).max(320),
  password: presentedPasswordSchema,
  /**
   * Supplied on the SECOND call, after a first attempt returned `mfa_required`. Re-sending the
   * password rather than exchanging a short-lived challenge token is the deliberate choice: a
   * challenge token is a third credential type to issue, store, expire, and revoke, and getting
   * any of that wrong is a full authentication bypass. The cost is one extra argon2 verify.
   */
  totpCode: totpCodeSchema.optional(),
  recoveryCode: recoveryCodeSchema.optional(),
  deviceId: z.string().max(128).optional(),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

/**
 * Binds a handoff code to the phone app that asked for it (PKCE, RFC 7636 "S256"), docs/adr/0038.
 *
 * The packaged app signs in through the system browser and gets its code back over a custom URL
 * scheme (`peopleorbit://`), which ANY app on the phone may claim. So the app invents a random
 * `appVerifier`, sends only its SHA-256 (`appChallenge`, base64url, always 43 characters) when the
 * sign-in starts, and shows the verifier again only when it redeems the code. A rogue app that
 * captures the code from the scheme cannot redeem it: it never saw the verifier.
 */
export const appChallengeSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const appVerifierSchema = z.string().regex(/^[A-Za-z0-9_-]{43,128}$/);

/**
 * One person in sign-up's "Add your team" step — a name and a mobile number, as the Android contact
 * picker (or the web's manual rows) gives them. `phone` is E.164; the client normalises it.
 */
export const registerTeamMemberSchema = z.object({
  name: z.string().trim().min(1).max(200),
  phone: phoneSchema,
});
export type RegisterTeamMember = z.infer<typeof registerTeamMemberSchema>;

/**
 * A company registering itself (Keka-style). Creates the Organization, the founding Person and their
 * Credential, AND the company's first legal entity, a "Head office" location and the owner's own
 * employment — so the owner is the first employee and Add Employee works straight away.
 *
 * The form still does not ask for a country. Country, currency, fiscal year and timezone are
 * DETECTED (`detectCompanyDefaults`): the mobile number's calling code, then the browser's timezone
 * (sent silently as `timezone`), then India. The owner reviews and changes them on first sign-in;
 * they stay editable until the entity's first payroll run.
 */
export const registerOrgBodySchema = z.object({
  companyName: z.string().trim().min(1).max(200),
  /**
   * Optional: the sign-up form does not ask for it. Sent when a sign-in provider (Zoho, Google)
   * already knows the person's name. Without it the owner is created as "User" and edits their
   * name from their profile. Split into Person's firstName/lastName server-side (first word /
   * remainder); the original string is kept verbatim as displayName.
   */
  ownerName: z.string().trim().min(1).max(200).optional(),
  orgSlug: orgSlugSchema,
  email: z.string().trim().pipe(emailSchema),
  /** E.164, required — the one channel that reaches the founder when email bounces or sits unread. */
  phone: phoneSchema,
  // There is deliberately no password field: sign-up is password-less. The founder is verified by
  // `phoneProof` (or `ssoProof`) and sets a password later, from the dashboard, through
  // `setInitialPassword`.
  /**
   * Proof from `ssoComplete` (`result: 'signup'`) that a sign-in provider verified this person's
   * email. Signed and short-lived by the API; `email` above must equal the one inside it.
   */
  ssoProof: z.string().min(20).max(2048).optional(),
  /**
   * Only with `ssoProof`, from the phone app's browser sign-up: binds the `handoffCode` in the
   * response to the app (see `appChallengeSchema`).
   */
  appChallenge: appChallengeSchema.optional(),
  /**
   * Proof that the founder controls `phone`: the ID token Firebase Auth issues once the SMS code was
   * entered (docs/adr/0036-signup-verification.md). The API checks its signature, freshness and that
   * its number equals `phone`. Required exactly when the deployment has phone verification configured
   * (`signupOptions` says so); ignored otherwise.
   */
  phoneProof: z.string().min(20).max(4096).optional(),
  /** Signup clickwrap: registration is refused unless the founder accepted the terms. */
  termsAccepted: z.literal(true),
  termsVersion: z.string().trim().min(1).max(50),
  /**
   * The browser's IANA timezone, sent by the sign-up page without asking. Only a hint: used when it
   * belongs to the detected country, ignored otherwise.
   */
  timezone: z.string().trim().max(64).optional(),
  /**
   * "Add your team" (sign-up step 3, docs/adr/0036-signup-verification.md): up to
   * `FREE_TRIAL_EMPLOYEE_LIMIT` people the owner picked from their phone's contacts (or typed in on
   * the web). Each becomes a PHONE-ONLY employee in the same request — no email, joining today, on
   * the company default shift, no invite email sent. The owner then texts each one their sign-in
   * link from their own phone (Home's "Text your team their invite"). Omitted or empty = skipped.
   *
   * The array refuses duplicate numbers on its own; a member whose number is the owner's own `phone`
   * is refused by `refineRegisterOrgTeam`, which the route body (`registerOrgRequestSchema`) applies.
   * It is not applied here so this object stays extendable (the web form `.extend`s it).
   */
  team: z
    .array(registerTeamMemberSchema)
    .max(FREE_TRIAL_EMPLOYEE_LIMIT)
    .superRefine((members, ctx) => {
      const seen = new Set<string>();
      members.forEach((member, index) => {
        if (seen.has(member.phone)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [index, 'phone'],
            message: 'This number is already in your team',
          });
        }
        seen.add(member.phone);
      });
    })
    .optional(),
});
export type RegisterOrgRequest = z.infer<typeof registerOrgBodySchema>;

/**
 * The cross-field rule `registerOrgBodySchema.team` cannot express alone: no team member may carry
 * the owner's own number (the owner is already the first employee). Exported so a client that
 * extends `registerOrgBodySchema` can apply the very same rule to its form.
 */
export function refineRegisterOrgTeam(
  value: { phone: string; team?: readonly { phone: string }[] | undefined },
  ctx: z.RefinementCtx,
): void {
  value.team?.forEach((member, index) => {
    if (member.phone === value.phone) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['team', index, 'phone'],
        message: 'This is your own number — you are already added',
      });
    }
  });
}

/** What `auth.register` actually validates: the body above plus `refineRegisterOrgTeam`. */
export const registerOrgRequestSchema = registerOrgBodySchema.superRefine(refineRegisterOrgTeam);

export const registerOrgResponseSchema = z.object({
  orgSlug: z.string(),
  email: z.string(),
  /**
   * Present when the founder is verified at registration (`ssoProof`, or the phone OTP `phoneProof`):
   * there is no link to click — this is the one-time handoff code (same as `sessionHandoff`) that
   * signs the founder in on their new company's own subdomain. Absent when verification is still
   * pending, which is polled through `signupTicket`.
   */
  handoffCode: z.string().min(16).max(128).optional(),
  /**
   * Only when verification is still pending: the "check your email" screen presents it to
   * `signupStatus` until the link has been followed, then is signed in by that answer. It never
   * leaves the browser that registered, so it grants nothing the registration did not.
   */
  signupTicket: z.string().min(16).max(128).optional(),
});

/**
 * What the sign-up page needs to know before it starts. `phoneVerification` is null when the
 * deployment has no phone verification configured, and the page then skips the mobile code step.
 * Every value in it is the Firebase WEB config — public by design, it ships in any page that uses
 * Firebase — so serving it from the API is a convenience (one place to set it, no rebuild), not a leak.
 */
export const phoneVerificationConfigSchema = z.object({
  provider: z.literal('firebase'),
  apiKey: z.string().min(1),
  authDomain: z.string().min(1),
  projectId: z.string().min(1),
  appId: z.string().min(1),
});

export const signupOptionsResponseSchema = z.object({
  phoneVerification: phoneVerificationConfigSchema.nullable(),
});
export type SignupOptions = z.infer<typeof signupOptionsResponseSchema>;

export const signupStatusBodySchema = z.object({ ticket: z.string().min(16).max(128) });

/**
 * `pending` until the verification link has been followed — on any device — then `verified` with the
 * same one-time handoff code every other sign-in ends with, redeemed on the new company's subdomain.
 */
export const signupStatusResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('pending') }),
  z.object({
    status: z.literal('verified'),
    orgSlug: z.string(),
    handoffCode: z.string().min(16).max(128),
  }),
]);
export type SignupStatus = z.infer<typeof signupStatusResponseSchema>;

/** The token embedded in the emailed verification link. */
export const verificationTokenSchema = z.string().min(32).max(512);

export const verifyEmailBodySchema = z.object({ token: verificationTokenSchema });

/**
 * Verifying and signing in happen in one step: proving control of the mailbox after already
 * proving the password is the same trust level as an ordinary login, so there is no reason to make
 * the owner type their password a second time. Same shape as login's `authenticated` branch —
 * never `mfa_required`, since a just-registered account has nothing enrolled yet to challenge.
 */
export const verifyEmailResponseSchema = z.object({
  tokens: tokenPairSchema,
  person: authenticatedPersonSchema,
});

export const resendVerificationBodySchema = z.object({
  /** Same subdomain fallback as `loginRequestSchema.orgSlug` above. */
  orgSlug: orgSlugSchema.optional(),
  email: z.string().trim().pipe(emailSchema),
});

export const forgotPasswordBodySchema = z.object({
  /** Same subdomain fallback as `loginRequestSchema.orgSlug` above. */
  orgSlug: orgSlugSchema.optional(),
  email: z.string().trim().pipe(emailSchema),
});

/** base64url of 32 random bytes, same shape as `verificationTokenSchema` — a separate name
 *  because the two are issued under separate secrets and must stay conceptually distinct even
 *  though the string shape happens to match. */
export const passwordResetTokenSchema = z.string().min(32).max(512);

export const resetPasswordBodySchema = z.object({
  token: passwordResetTokenSchema,
  newPassword: newPasswordSchema,
});
export type ResetPasswordRequest = z.infer<typeof resetPasswordBodySchema>;

/**
 * "Forgot password" by mobile code (ADR 0036): the SMS proof for the number the account itself
 * proved at sign-up stands in for the emailed link. Reaches only an account whose credential holds
 * that exact proven number in this company, so it also rescues an owner who mistyped their email.
 */
export const resetPasswordByPhoneBodySchema = z.object({
  /** Same subdomain fallback as `loginRequestSchema.orgSlug`. */
  orgSlug: orgSlugSchema.optional(),
  /** E.164; must equal the number inside `phoneProof`. */
  phone: phoneSchema,
  /** The Firebase ID token from the SMS code, as at sign-up. Single-use. */
  phoneProof: z.string().min(20).max(4096),
  newPassword: newPasswordSchema,
});
export type ResetPasswordByPhoneRequest = z.infer<typeof resetPasswordByPhoneBodySchema>;

/**
 * Same reasoning as `verifyEmailResponseSchema`: proving control of the mailbox by following this
 * link IS proving identity, so resetting and signing in happen in one step rather than asking for
 * the password a second time — a password the caller, by definition, just forgot.
 */
export const resetPasswordResponseSchema = z.object({
  tokens: tokenPairSchema,
  person: authenticatedPersonSchema,
});

export const refreshRequestSchema = z.object({ refreshToken: refreshTokenSchema });

/**
 * A landing on the SAME app, at a different tenant's subdomain, that already carries a working
 * session — instead of a bare redirect that would leave the browser on the new origin logged out.
 *
 * WHY THIS EXISTS. Sessions live in the browser's localStorage, which is scoped per origin. A
 * caller who logs in from the bare app domain (or the wrong tenant's subdomain) and types
 * `arham`'s workspace slug gets tokens that are only ever saved to the origin they're standing on
 * — redirecting to `arham.<appDomain>` afterward would strand those tokens behind and land the
 * browser on a fresh, signed-out origin. Handing the destination origin its OWN token pair is the
 * only way to make "log in, land on your company's URL" actually work.
 *
 * WHY A CODE, NOT THE TOKENS THEMSELVES, CROSS THE REDIRECT. Same reasoning as every other bearer
 * secret in this system (see @hrms/auth's tokens.ts header comment): a value that has to survive a
 * URL is a value a browser history entry, a screen share, or a referrer-less-but-still-logged
 * proxy could see. The code is opaque, single-use, and expires in under a minute — worthless to
 * anyone who captures it a moment later, and it is exchanged for a token pair only once, server
 * side, the same shape as redeeming a verification or reset link.
 */
export const sessionHandoffResponseSchema = z.object({ code: z.string().min(16).max(128) });

export const redeemSessionHandoffBodySchema = z.object({
  code: z.string().min(16).max(128),
  /**
   * Required when the code was minted for an `appChallenge` (the API answers 401 otherwise, and the
   * code is spent either way); ignored for codes minted without one.
   */
  appVerifier: appVerifierSchema.optional(),
});

/** Same shape as verifyEmail/resetPassword's response — landing here IS signing in. */
export const redeemSessionHandoffResponseSchema = z.object({
  tokens: tokenPairSchema,
  person: authenticatedPersonSchema,
});

// ---------------------------------------------------------------------------
// Sign in / sign up with an identity provider (Zoho, Google) — docs/adr/0033-zoho-sign-in.md
// ---------------------------------------------------------------------------

/**
 * The providers this API knows how to talk to. Adding one is a line here, an entry in apps/api's
 * provider registry, and its credentials in env; which of them are actually usable on a deployment
 * is `ssoProviders`'s answer, so the login and sign-up pages show exactly those buttons.
 */
export const SSO_PROVIDERS = ['zoho', 'google'] as const;
export const ssoProviderSchema = z.enum(SSO_PROVIDERS);
export type SsoProvider = z.infer<typeof ssoProviderSchema>;

/** Why the person is going to the provider: to get into a company they have a login at, or to create one. */
export const ssoPurposeSchema = z.enum(['signin', 'signup']);
export type SsoPurpose = z.infer<typeof ssoPurposeSchema>;

export const ssoProvidersResponseSchema = z.object({ providers: z.array(ssoProviderSchema) });

/**
 * A path on THIS app to land on after sign-in. Same-origin paths only: it rides inside the signed
 * `state` and comes back as a redirect target, which is exactly where an open redirect would hide.
 */
export const postSignInPathSchema = z
  .string()
  .max(512)
  .refine((value) => value.startsWith('/') && !value.startsWith('//') && !value.includes('\\'), {
    message: 'Must be a path on this app, e.g. "/leave"',
  });

export const ssoStartBodySchema = z.object({
  provider: ssoProviderSchema,
  purpose: ssoPurposeSchema,
  /**
   * Optional: a workspace the person is already looking at (a company's own subdomain, or one typed
   * on the login form). It NARROWS a sign-in to that company; with none, every company they have a
   * login at is eligible. Ignored for sign-up, where the company does not exist yet.
   */
  orgSlug: orgSlugSchema.optional(),
  next: postSignInPathSchema.optional(),
  /**
   * Set by the packaged phone app, whose sign-in runs in the system browser and returns to the app
   * with a handoff code. The code that sign-in ends with is then redeemable only by whoever holds the
   * matching `appVerifier` (see `appChallengeSchema`). Rides inside the signed `state`.
   */
  appChallenge: appChallengeSchema.optional(),
});

/**
 * `url` is where the browser goes next (the provider's consent screen). `nonce` is NOT sent to the
 * provider: the caller keeps it in this browser's own storage and presents it again on the callback,
 * which is what proves the browser finishing a sign-in is the one that started it. The signed
 * `state` only carries its hash, so a callback planted into someone else's browser has nothing to
 * match.
 */
export const ssoStartResponseSchema = z.object({
  url: z.string().url(),
  nonce: z.string().min(16).max(128),
});

export const ssoCompleteBodySchema = z.object({
  provider: ssoProviderSchema,
  /** Both arrive as query parameters on the redirect from the provider. */
  code: z.string().min(1).max(2048),
  state: z.string().min(1).max(2048),
  nonce: z.string().min(16).max(128),
});

/** A company the person has a login at — only ever the caller's OWN, only after the provider vouched for them. */
export const ssoWorkspaceSchema = z.object({ slug: z.string(), name: z.string() });

const handoffCodeSchema = z.string().min(16).max(128);

/**
 * What a verified identity turns into. A union, so a client cannot compile while ignoring a branch.
 *
 * Codes are not tokens: `handoffCode` is the same one-time code `sessionHandoff` mints, redeemed by
 * the existing `redeemSessionHandoff` on the company's own subdomain, because a session lives in
 * per-origin storage and the callback arrives on the bare app domain.
 */
export const ssoCompleteResponseSchema = z.discriminatedUnion('result', [
  z.object({
    result: z.literal('signed_in'),
    orgSlug: z.string(),
    handoffCode: handoffCodeSchema,
    next: postSignInPathSchema,
  }),
  z.object({
    result: z.literal('choose_workspace'),
    /** Single-use, minutes long: redeemed by `ssoChoose` with the company the person picks. */
    ticket: z.string().min(16).max(128),
    workspaces: z.array(ssoWorkspaceSchema).min(2),
    next: postSignInPathSchema,
  }),
  z.object({
    result: z.literal('signup'),
    /** Present to `register` as `ssoProof`. */
    proof: z.string().min(20).max(2048),
    /** Prefill only; the proof, not these, is what `register` trusts. */
    name: z.string(),
    email: z.string(),
    /**
     * Present only when this sign-up began in the phone app (an `appChallenge` was set at
     * `ssoStart`): `proof`/`name`/`email` above travel bound to the app instead, as this code,
     * because the callback that produced them is running in the SYSTEM BROWSER, not the app, and
     * the only way out is the custom-scheme hop any app on the phone could claim (see
     * `appChallengeSchema`). Redeemed by `redeemSsoSignupHandoff`, which returns the same three
     * fields once the app proves it holds the matching verifier. A browser sign-up never gets one
     * and uses `proof`/`name`/`email` directly, in this same response.
     */
    handoffCode: handoffCodeSchema.optional(),
  }),
]);
export type SsoCompleteResponse = z.infer<typeof ssoCompleteResponseSchema>;

/**
 * Redeems the `handoffCode` above. Unlike `redeemSessionHandoff`, this code is never minted
 * unbound — `appVerifier` is required, not optional — because it only ever exists for a sign-up
 * the phone app itself started.
 */
export const redeemSsoSignupHandoffBodySchema = z.object({
  code: handoffCodeSchema,
  appVerifier: appVerifierSchema,
});

/** Exactly the fields `register` needs: `proof` as `ssoProof`, the rest to prefill the form. */
export const redeemSsoSignupHandoffResponseSchema = z.object({
  provider: ssoProviderSchema,
  proof: z.string().min(20).max(2048),
  name: z.string(),
  email: z.string(),
});

export const ssoChooseBodySchema = z.object({
  ticket: z.string().min(16).max(128),
  orgSlug: orgSlugSchema,
});

export const ssoChooseResponseSchema = z.object({
  orgSlug: z.string(),
  handoffCode: handoffCodeSchema,
  next: postSignInPathSchema,
});

/**
 * One of the caller's OWN accounts in a sibling organisation, reachable because a platform admin
 * has linked it to the account the caller is presently signed in as (see
 * `packages/db/prisma/schema.prisma`'s `CrossOrgIdentityLink`). Deliberately not the caller's
 * current org — `listLinkedOrganizations` reports only the OTHER places she can switch to.
 */
export const linkedOrganizationSchema = z.object({
  id: orgIdSchema,
  name: z.string(),
  slug: z.string(),
});

export const listLinkedOrganizationsResponseSchema = z.object({
  organizations: z.array(linkedOrganizationSchema),
});

/**
 * Same code-and-redirect shape as `sessionHandoffResponseSchema` above, and redeemed by the very
 * same `redeemSessionHandoff` endpoint — a cross-org switch is a handoff whose destination
 * identity is a *linked* Person rather than the caller's own, so nothing downstream of the code
 * needs to know the difference.
 */
export const switchToLinkedOrganizationResponseSchema = z.object({
  code: z.string().min(16).max(128),
  organization: linkedOrganizationSchema,
});

export const changePasswordRequestSchema = z.object({
  /**
   * Required for a voluntary change. Left out only on the forced change after a temporary password
   * (`mustChangePassword`): the person has just signed in with that password, so asking for it again
   * proves nothing. The API refuses an omitted value for anyone not under that flag.
   */
  currentPassword: presentedPasswordSchema.optional(),
  newPassword: newPasswordSchema,
});

/**
 * First password for a credential that has none (a password-less sign-up). No `currentPassword`:
 * there is nothing to present. The signed-in session is the proof.
 */
export const setInitialPasswordRequestSchema = z.object({ newPassword: newPasswordSchema });

export const mfaVerifyRequestSchema = z.object({ code: totpCodeSchema });

export const mfaDisableRequestSchema = z.object({
  /**
   * Re-authentication, not authorisation. The bearer token already proves the session; this proves
   * the person holding it is who opened it, which is what stops an unlocked laptop from becoming a
   * permanent MFA removal.
   */
  password: presentedPasswordSchema,
  totpCode: totpCodeSchema.optional(),
  recoveryCode: recoveryCodeSchema.optional(),
});

// ---------------------------------------------------------------------------
// The contract
// ---------------------------------------------------------------------------

/**
 * Error bodies are declared per status so a client can narrow on `status` and still read
 * `body.code`. The middleware in apps/api produces exactly this shape for every AppError.
 */
const errors = {
  400: errorBodySchema,
  401: errorBodySchema,
  403: errorBodySchema,
  409: errorBodySchema,
  422: errorBodySchema,
  429: errorBodySchema,
} as const;

/**
 * No route here declares an `authorization` header.
 *
 * The client injects it from the token store for every request, so declaring it would force each
 * call site to pass a token it does not hold — and would tempt someone into trusting a
 * contract-parsed header rather than the verified JWT. Authentication is middleware's job.
 */
export const authContract = c.router(
  {
    register: {
      method: 'POST',
      path: '/register',
      body: registerOrgRequestSchema,
      responses: {
        201: registerOrgResponseSchema,
        // 409 covers a taken workspace slug — the one thing this route validates against existing
        // state rather than shape.
        ...errors,
      },
      summary:
        'Register a new company: creates the org and its founding owner. There is no password at ' +
        'sign-up — the owner is verified by the phone OTP proof (`phoneProof`) or `ssoProof` and ' +
        'sets a password later via `setInitialPassword`. An optional `team` (up to the free-trial ' +
        'cap) is created as phone-only employees in the same request.',
      metadata: routeMeta('public'),
    },

    signupOptions: {
      method: 'GET',
      path: '/signup/options',
      responses: { 200: signupOptionsResponseSchema, ...errors },
      summary:
        'What the sign-up page needs before it starts: whether the mobile number is verified with ' +
        'an SMS code, and the public Firebase web config to do it with. Nothing about any company.',
      metadata: routeMeta('public'),
    },

    signupStatus: {
      method: 'POST',
      path: '/signup/status',
      body: signupStatusBodySchema,
      responses: {
        200: signupStatusResponseSchema,
        // 401 `details.reason: 'signup_ticket_expired'`: unknown, used, or past its lifetime.
        ...errors,
      },
      summary:
        'Poll from the sign-up page\'s "check your email" screen with the ticket `register` ' +
        'returned: pending until the emailed link is followed (on any device), then a one-time ' +
        'handoff code that signs the founder in on their new workspace',
      metadata: routeMeta('public'),
    },

    verifyEmail: {
      method: 'POST',
      path: '/verify-email',
      body: verifyEmailBodySchema,
      responses: {
        200: verifyEmailResponseSchema,
        // 400 `details.reason: 'email_already_verified'` for a used link whose email is verified,
        // so the page can say "already verified, sign in" instead of "this link is invalid".
        ...errors,
      },
      summary: 'Activate an account via its emailed link and sign in',
      metadata: routeMeta('public'),
    },

    resendVerification: {
      method: 'POST',
      path: '/resend-verification',
      body: resendVerificationBodySchema,
      responses: { 200: z.object({ ok: z.literal(true) }), ...errors },
      summary:
        'Re-send the verification email. Always reports ok, whether or not the account exists ' +
        'or is already verified — the endpoint must not become a way to enumerate either.',
      metadata: routeMeta('public'),
    },

    forgotPassword: {
      method: 'POST',
      path: '/forgot-password',
      body: forgotPasswordBodySchema,
      responses: { 200: z.object({ ok: z.literal(true) }), ...errors },
      summary:
        'Request a password-reset email. Always reports ok, whether or not the account exists — ' +
        'same enumeration-safety reasoning as resendVerification.',
      metadata: routeMeta('public'),
    },

    resetPassword: {
      method: 'POST',
      path: '/reset-password',
      body: resetPasswordBodySchema,
      responses: { 200: resetPasswordResponseSchema, ...errors },
      summary: 'Set a new password via the emailed link, sign in, and revoke every other session',
      metadata: routeMeta('public'),
    },

    resetPasswordByPhone: {
      method: 'POST',
      path: '/reset-password/phone',
      body: resetPasswordByPhoneBodySchema,
      responses: { 200: z.object({ ok: z.literal(true) }), ...errors },
      summary:
        'Set a new password with an SMS proof for the mobile number the account proved at sign-up, ' +
        'and revoke every session. The person then signs in on their company address.',
      metadata: routeMeta('public'),
    },

    login: {
      method: 'POST',
      path: '/login',
      body: loginRequestSchema,
      responses: { 200: loginResultSchema, ...errors },
      summary: 'Exchange org slug, email, and password for a token pair',
      metadata: routeMeta('public'),
    },

    refresh: {
      method: 'POST',
      path: '/refresh',
      body: refreshRequestSchema,
      responses: { 200: z.object({ tokens: tokenPairSchema }), ...errors },
      summary: 'Rotate a refresh token for a new token pair',
      metadata: routeMeta('public'),
    },

    sessionHandoff: {
      method: 'POST',
      path: '/session-handoff',
      body: c.noBody(),
      responses: { 200: sessionHandoffResponseSchema, ...errors },
      summary:
        'Mint a one-time code that redeems for a fresh token pair on another tenant subdomain, ' +
        'and revoke the session this call authenticated with — the code is meant to replace it, ' +
        'not sit alongside it',
      metadata: routeMeta(null),
    },

    redeemSessionHandoff: {
      method: 'POST',
      path: '/session-handoff/redeem',
      body: redeemSessionHandoffBodySchema,
      responses: { 200: redeemSessionHandoffResponseSchema, ...errors },
      summary: 'Exchange a one-time handoff code for a token pair, on the destination origin',
      metadata: routeMeta('public'),
    },

    ssoProviders: {
      method: 'GET',
      path: '/sso/providers',
      responses: { 200: ssoProvidersResponseSchema, ...errors },
      summary:
        'Which sign-in providers this deployment has configured, so the login and sign-up pages ' +
        'show exactly those buttons. Nothing about any company.',
      metadata: routeMeta('public'),
    },

    ssoStart: {
      method: 'POST',
      path: '/sso/start',
      body: ssoStartBodySchema,
      responses: {
        200: ssoStartResponseSchema,
        // 409: the provider is not configured on this deployment.
        ...errors,
      },
      summary:
        "Begin signing in or up with a provider: returns the provider's consent URL and this " +
        "attempt's nonce",
      metadata: routeMeta('public'),
    },

    ssoComplete: {
      method: 'POST',
      path: '/sso/complete',
      body: ssoCompleteBodySchema,
      responses: {
        200: ssoCompleteResponseSchema,
        // 401 `details.reason`: `sso_unverified` (state, nonce, code or id_token did not check out),
        // or `sso_no_account` (a verified identity with no active login anywhere it may sign in).
        ...errors,
      },
      summary:
        'Finish a provider sign-in: verify the redirect, then either sign the person into the one ' +
        'company they have a login at, ask which of several, or (for sign-up) return a proof to ' +
        'register with',
      metadata: routeMeta('public'),
    },

    redeemSsoSignupHandoff: {
      method: 'POST',
      path: '/sso/signup-handoff/redeem',
      body: redeemSsoSignupHandoffBodySchema,
      responses: { 200: redeemSsoSignupHandoffResponseSchema, ...errors },
      summary:
        "The phone app's half of a sign-up that ran in the system browser: exchange the handoff " +
        'code `ssoComplete` minted, with the verifier that proves this app started it, for the ' +
        'proof to register with.',
      metadata: routeMeta('public'),
    },

    ssoChoose: {
      method: 'POST',
      path: '/sso/choose',
      body: ssoChooseBodySchema,
      responses: { 200: ssoChooseResponseSchema, ...errors },
      summary:
        'Pick which company to enter after a provider sign-in that matched several. Redeems the ' +
        'single-use ticket `ssoComplete` returned.',
      metadata: routeMeta('public'),
    },

    listLinkedOrganizations: {
      method: 'GET',
      path: '/me/linked-organizations',
      responses: { 200: listLinkedOrganizationsResponseSchema, ...errors },
      summary:
        "List the caller's OWN accounts in other organisations that a platform admin has linked " +
        "to this one, for a 'switch company' menu. Empty for everyone except the small number of " +
        'people who genuinely hold verified accounts across sibling companies.',
      metadata: routeMeta(null),
    },

    switchToLinkedOrganization: {
      method: 'POST',
      path: '/me/linked-organizations/:orgId/switch',
      pathParams: z.object({ orgId: orgIdSchema }),
      body: c.noBody(),
      responses: { 200: switchToLinkedOrganizationResponseSchema, ...errors },
      summary:
        "Mint a one-time handoff code for the caller's linked account in :orgId. Refuses with 403 " +
        'if no platform admin has linked the two accounts. The CURRENT session is left alone — ' +
        "unlike sessionHandoff's same-identity subdomain hop, this opens a second, independent " +
        'account rather than moving the first one.',
      metadata: routeMeta(null),
    },

    logout: {
      method: 'POST',
      path: '/logout',
      /**
       * No body: the bearer token names the session to end. Accepting a refresh token here would
       * mean the endpoint could be asked to revoke a session the caller does not own, and the only
       * thing standing between that and a denial-of-service on another employee would be a
       * server-side ownership check we would have to remember to write.
       */
      body: c.noBody(),
      responses: { 200: z.object({ ok: z.literal(true) }), ...errors },
      summary: 'Revoke the current session',
      metadata: routeMeta(null),
    },

    logoutAll: {
      method: 'POST',
      path: '/logout-all',
      body: c.noBody(),
      responses: { 200: z.object({ ok: z.literal(true), revoked: z.number().int() }), ...errors },
      summary: 'Revoke every session this person holds, on every device',
      metadata: routeMeta(null),
    },

    me: {
      method: 'GET',
      path: '/me',
      responses: { 200: meResponseSchema, ...errors },
      summary: 'The authenticated person, their grants, and the permissions those imply',
      metadata: routeMeta(null),
    },

    changePassword: {
      method: 'POST',
      path: '/password',
      body: changePasswordRequestSchema,
      responses: {
        200: z.object({ ok: z.literal(true), otherSessionsRevoked: z.number().int() }),
        ...errors,
      },
      summary: 'Change the password and revoke every other session',
      metadata: routeMeta(null),
    },

    setInitialPassword: {
      method: 'POST',
      path: '/set-initial-password',
      body: setInitialPasswordRequestSchema,
      responses: { 200: z.object({ ok: z.literal(true) }), ...errors },
      summary:
        'Set the first password on a credential that has none (password-less sign-up). Only ' +
        'allowed while the caller has no password yet; otherwise 409 (use changePassword).',
      metadata: routeMeta(null),
    },

    mfaEnrol: {
      method: 'POST',
      path: '/mfa/enrol',
      body: c.noBody(),
      responses: {
        200: z.object({ secret: z.string(), uri: z.string() }),
        ...errors,
      },
      summary: 'Begin TOTP enrolment — returns a secret that is not yet active',
      metadata: routeMeta(null),
    },

    mfaVerify: {
      method: 'POST',
      path: '/mfa/verify',
      body: mfaVerifyRequestSchema,
      responses: {
        /** Recovery codes are returned in clear exactly once; only their hashes are stored. */
        200: z.object({ enabled: z.literal(true), recoveryCodes: z.array(z.string()) }),
        ...errors,
      },
      summary: 'Activate TOTP by proving possession of the enrolled secret',
      metadata: routeMeta(null),
    },

    mfaDisable: {
      method: 'POST',
      path: '/mfa/disable',
      body: mfaDisableRequestSchema,
      responses: { 200: z.object({ enabled: z.literal(false) }), ...errors },
      summary: 'Turn TOTP off after re-authentication',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/auth' },
);

export type AuthContract = typeof authContract;
